/**
 * Backend Health Indicator
 *
 * A subtle, non-intrusive indicator showing backend connection status.
 * Used on auth pages (Login, Register) to inform users before they submit forms.
 */

import { useTranslation } from 'react-i18next';
import { useBackendHealth } from '@/hooks/useBackendHealth';
import { cn } from '@/lib/utils';

interface BackendHealthIndicatorProps {
  /** Additional CSS classes */
  className?: string;
}

export function BackendHealthIndicator({ className }: BackendHealthIndicatorProps) {
  const { t } = useTranslation();
  const { isHealthy, isStarting, isChecking, error } = useBackendHealth();

  // Don't show anything while checking initially
  if (isHealthy === null && isChecking) {
    return (
      <div className={cn('flex items-center justify-center gap-2', className)}>
        <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
        <span className="text-xs text-neutral-500">
          {t('health.checking', 'Checking connection...')}
        </span>
      </div>
    );
  }

  // Backend is starting (loading models)
  if (isStarting) {
    return (
      <div className={cn('flex items-center justify-center gap-2', className)}>
        <div className="w-2 h-2 rounded-full bg-citation animate-pulse" />
        <span className="text-xs text-citation-700">
          {t('health.starting', 'Server starting...')}
        </span>
      </div>
    );
  }

  // Backend is healthy - show green indicator briefly, then fade
  if (isHealthy === true) {
    return (
      <div className={cn('flex items-center justify-center gap-2', className)}>
        <div className="w-2 h-2 rounded-full bg-green-500" />
        <span className="text-xs text-green-600">
          {t('health.connected', 'Connected')}
        </span>
      </div>
    );
  }

  // Backend is unhealthy - show warning (but softer if no explicit error)
  if (isHealthy === false) {
    // If no error message, we're likely in cold start - show softer message
    const showHardError = error && !isChecking;

    return (
      <div className={cn('flex flex-col items-center gap-1', className)}>
        <div className="flex items-center gap-2">
          <div className={cn(
            'w-2 h-2 rounded-full animate-pulse',
            showHardError ? 'bg-red-500' : 'bg-amber-500'
          )} />
          <span className={cn(
            'text-xs font-medium',
            showHardError ? 'text-red-600' : 'text-amber-600'
          )}>
            {showHardError
              ? t('health.disconnected', 'Server unavailable')
              : t('health.connecting', 'Connecting to server...')}
          </span>
        </div>
        {isChecking && (
          <span className="text-[10px] text-neutral-400">
            {t('health.retrying', 'Retrying connection...')}
          </span>
        )}
      </div>
    );
  }

  return null;
}
